import React, {useCallback, useEffect, useState} from 'react';
import { useTitle } from "fearless-common-ui";
import {concepts, explanations, Group, Subscription, User, GroupRole} from "fearless-common";
import {useParams} from "react-router-dom";
import {useNavigate} from "react-router";
import {PencilSquare} from "react-bootstrap-icons";
import Modal from "react-bootstrap/Modal";
import axios from "axios";
import Button from "react-bootstrap/Button";
import {Accordion, Alert, Col, Row} from "react-bootstrap";
import {Pages} from "../components/layout";
import useAuth from "../components/hooks/useAuth";
import useGet from "../components/hooks/useGet";
import {backendEndpoints} from "../libs/backendEndpoints";
import Header from "../components/Header";
import NewGroupButtonAndModal from "../components/groups/NewGroupButtonAndModal";
import {routes} from "../utils";
import Loading from "../components/Loading";
import DotsMenu from "../components/layout/DotsMenu";
import InfoTooltip from "../components/layout/InfoTooltip";
import GroupEdit from "./partials/GroupEdit";
import BackendForm from "../components/forms/BackendForm";
import FloatingLabelField from "../components/forms/FloatingLabelField";
import styles from "./partials/Groups.module.scss";
import GroupDeleteButton from "../components/groups/GroupDeleteButton";
import {useConfirm} from "../components/contexts/ConfirmationModalContext";
import SeatsSpan from "../components/subscriptions/SeatsSpan";
import ManageGroupSubscriptionModal from "../components/subscriptions/ManageGroupSubscriptionModal";
import GroupPermissionsModal from '../components/groups/GroupPermissionsModal';

const fixedKeys = {
    loading: 'loading',
    create: 'create'
}
type GroupWithRelations = (Group & { subscriptions: Subscription[]; users?: Array<User & { pivot: { role: GroupRole }}> });

const composeGroupKey = (id: number | string) => `group-${id}`;
const decomposeGroupKey = (string: string) => string.replace('group-', '');

function GroupEditModal({ group, onSuccess, onClose }: { group: Group; onSuccess: () => void; onClose: () => void }) {
    return (
        <Modal onHide={onClose} show>
            <Modal.Header closeButton>
                <Modal.Title>Edit {concepts.Group.toLowerCase()}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <BackendForm
                    initialValues={group}
                    targetEndpoint={backendEndpoints.updateGroup(group.id)}
                    targetMethod="PUT"
                    enableReinitialize
                    showFooter={false}
                    onSuccess={() => {
                        onSuccess();
                        setTimeout(() => {
                            onClose();
                        }, 1000);
                    }}
                >
                    <FloatingLabelField name="name" label="Name" autoFocus />
                    <BackendForm.Footer
                        showError={false}
                        marginTop={false}
                        buttonText="Edit"
                        className={styles.SubmitButton}
                    />
                </BackendForm>
            </Modal.Body>
        </Modal>
    );
}
function DeleteGroupModal({ group, onClose }: { group: Group; onClose: () => void }) {
    return (
        <Modal onHide={onClose} show size="sm">
            <Modal.Header closeButton>
                <Modal.Title className="text-center flex-grow-1">Delete {concepts.Group.toLowerCase()}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>{explanations.deleteGroup}.</p>
                <div className="text-center">
                    <GroupDeleteButton group={group} size="lg" variant="danger" />
                </div>
            </Modal.Body>
        </Modal>
    )
}

/**
 * Modal for a single member of a group to leave the group.
 */
function LeaveGroupModal({ group, onClose }: { group: GroupWithRelations; onClose: () => void }) {
    const {open} = useConfirm();
    const {user} = useAuth();

    const leaveGroup = useCallback(() => {
        return axios.delete(backendEndpoints.leaveGroup(group.id), { data: { user_id: user?.id }})
            .then(() => {
                window.location.href = routes.groups;
            });
    }, [group.id, user?.id]);

    return (
        <Modal onHide={onClose} show size="sm">
            <Modal.Header closeButton>Exit {concepts.Group.toLowerCase()}</Modal.Header>
            <Modal.Body>
                <p>If you leave the ${concepts.Group.toLowerCase()}, you will lose access to its data and its subscription.</p>
                <Button
                    onClick={() => {
                        open('Are you sure you want to leave this group?', leaveGroup, true);
                    }}
                    variant="outline-danger"
                >
                    Leave {concepts.Group.toLowerCase()}
                </Button>
            </Modal.Body>
        </Modal>
    );
}


const modalKeys = {
    None: '',
    GroupEdit: 'group-edit',
    ManageSubscription: 'manage-subscription',
    ManagePermissions: 'manage-permissions',
    DeleteGroup: 'exit-team',
    LeaveGroup: 'leave-team',
}

/**
 * Overview of the logged-in user's groups.
 */
export default function LoggedInUserGroups() {
    const { groupId } = useParams();
    const [key, setKey] = useState(fixedKeys.loading);
    const [modalKey, setModalKey] = useState(modalKeys.None);
    const [activeGroup, setActiveGroup] = useState<GroupWithRelations | null>(null);
    const navigate = useNavigate();
    const { user } = useAuth()
    const params = {
        _with: ['users', 'subscriptions'],
        // Only get groups which could potentially have a subscription. For now,
        // these are only research groups, so group_type=null (See backend for values)
        group_type: 'null',
    };
    const { data: backendData, isLoading, error, reload } = useGet<GroupWithRelations[]>({
        endpoint: backendEndpoints.getGroups, params,
    });

    useEffect(() => {
        if (!isLoading && key === fixedKeys.loading) {
            if (Array.isArray(backendData) && backendData?.length > 0) {
                setKey(composeGroupKey(backendData[0].id));
            } else {
                setKey(fixedKeys.create);
            }
        }
    }, [isLoading, backendData]);

    useEffect(() => {
        if (groupId) {
            const newKey = composeGroupKey(groupId);
            if (newKey !== key) {
                if (Object.values(fixedKeys).indexOf(groupId) === -1) {
                    setKey(newKey);
                } else {
                    setKey(groupId);
                }
            }
        }
    }, [groupId, key]);

    useTitle(`My ${concepts.Groups.toLowerCase()}`);

    const resetActive = () => {
        setModalKey(modalKeys.None);
        setActiveGroup(null);
    }

    return (
        <Pages.UserBase header={concepts.Groups}>
            <div className="d-flex align-items-center justify-content-start gap-3">
                <Header component="h3" className="flex-grow-1 mb-0">{concepts.Groups}</Header>
                <div className="flex-grow-1 mb-3">
                    <NewGroupButtonAndModal
                        onSuccess={({ data }: { data: { id: number }}) => {
                            reload();

                            // Navigate to the newly created group.
                            if (data.id) navigate(routes.group(data.id))
                        }}
                    />
                </div>
            </div>
            {error && <Alert className="alert-danger">{error}</Alert>}
            {isLoading ? <Loading /> : (
                <Accordion
                    id="controlled-tab-example"
                    activeKey={key}
                    onSelect={(k) => {
                        if (typeof k === 'string') {
                            setKey(k);
                            navigate(routes.group(decomposeGroupKey(k)))
                        }
                    }}
                    className="mb-3"
                >
                    {Array.isArray(backendData) && backendData.map((group) => {
                        const options = [
                            {
                                label: <>Change name</>,
                                onClick: () => {
                                    setActiveGroup(group);
                                    setModalKey(modalKeys.GroupEdit);
                                },
                            },
                            {
                                label: `Manage ${concepts.Subscription.toLowerCase()}`,
                                onClick: () => {
                                    setActiveGroup(group);
                                    setModalKey(modalKeys.ManageSubscription);
                                },
                            },
                            {
                                label: `Manage permissions`,
                                onClick: () => {
                                    setActiveGroup(group);
                                    setModalKey(modalKeys.ManagePermissions);
                                },
                            },
                        ];
                        const role = group.users?.find(u => u.id === user?.id)?.pivot.role;
                        if (role === GroupRole.Manager) {
                            options.push(
                                {
                                    label: <span className="text-danger">Delete {concepts.Group.toLowerCase()}</span>,
                                    onClick: () => {
                                        setActiveGroup(group);
                                        setModalKey(modalKeys.DeleteGroup);
                                    },
                                }
                            );
                        }
                        if (role === GroupRole.Member) {
                            options.push(
                                {
                                    label: <span className="text-danger">Leave {concepts.Group.toLowerCase()}</span>,
                                    onClick: () => {
                                        setActiveGroup(group);
                                        setModalKey(modalKeys.LeaveGroup);
                                    },
                                }
                            )
                        }
                        return (
                            <Accordion.Item eventKey={composeGroupKey(group.id)} key={group.id}>
                                <Accordion.Header>
                                    <Row className="w-100 align-items-center">
                                        <Col className="flex-grow-1 d-flex justify-content-between align-items-center" sm={3}>
                                            <span>{group.name}</span>
                                            <span>
                                                <DotsMenu options={options} icon={<PencilSquare />} />
                                            </span>
                                        </Col>
                                        {group.total_seats && group.total_seats > 0 ? (
                                            <>
                                                <Col sm={3}>Total seats: <SeatsSpan.Total totalSeats={group.total_seats} /></Col>
                                                <Col sm={3}>Occupied seats: {group.used_seats}</Col>
                                                <Col sm={3}>
                                                    Remaining seats:&nbsp;
                                                    {typeof group.used_seats === 'number' && <SeatsSpan.Remaining totalSeats={group.total_seats} usedSeats={group.used_seats} />}
                                                </Col>
                                            </>
                                        ) : <Col sm={9} />}
                                        {group.total_seats === -1 && (
                                            <>
                                                <Col>
                                                    Total seats: <SeatsSpan.Total totalSeats={group.total_seats} />
                                                    <InfoTooltip>{explanations.MonthlySubscriptionPayAsYouGo}</InfoTooltip>
                                                </Col>
                                                <Col>Occupied seats: {group.used_seats}</Col>
                                            </>
                                        )}
                                    </Row>
                                </Accordion.Header>
                                {key === composeGroupKey(group.id) && (
                                    <Accordion.Body className="pe-5">
                                        <GroupEdit
                                            groupId={group.id}
                                            onSuccess={() => reload()}
                                        />
                                    </Accordion.Body>
                                )}
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}
            {activeGroup && (
              <>
                {modalKey === modalKeys.GroupEdit &&
                    <GroupEditModal group={activeGroup} onSuccess={reload} onClose={resetActive}/>}
                {modalKey === modalKeys.ManageSubscription &&
                    <ManageGroupSubscriptionModal
                      group={activeGroup}
                      onClose={() => {
                          resetActive();
                          reload();
                      }}
                    />
                }
                {modalKey === modalKeys.DeleteGroup &&
                    <DeleteGroupModal group={activeGroup} onClose={resetActive} />}
                {modalKey === modalKeys.LeaveGroup &&
                    <LeaveGroupModal group={activeGroup} onClose={resetActive} />}
                {modalKey === modalKeys.ManagePermissions && (
                    <GroupPermissionsModal group={activeGroup} onClose={resetActive} />
                )}
              </>
            )}
        </Pages.UserBase>
    );
}
